import Thumbnail from "../../common/Thumbnail";

export const formatBytes = (bytes) => {
  if (typeof bytes !== "number" || isNaN(bytes)) return "N/A";
  return `${(bytes / 1024).toFixed(2)} KB`;
};

export const formatTime = (ms) => {
  if (typeof ms !== "number" || isNaN(ms)) return "N/A";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
};

/** Short label for HTML snippets – avoids full markup in tight table cells. */
export function summarizeNodeSnippet(snippet) {
  if (!snippet) return null;
  const compact = String(snippet).replace(/\s+/g, " ").trim();
  const tag = compact.match(/^<(\w+)/)?.[1];
  const attr =
    compact.match(/\b(?:src|poster|href)=["']([^"']+)["']/i)?.[1] ||
    compact.match(/\bdata-src=["']([^"']+)["']/i)?.[1];
  if (tag && attr) {
    const filename = attr.split("/").pop()?.split("?")[0] || attr;
    return `<${tag} … ${filename}>`;
  }
  if (tag) return `<${tag} …>`;
  return compact.length > 72 ? `${compact.slice(0, 72)}…` : compact;
}

export function extractSubItemReason(subItem) {
  if (!subItem || typeof subItem !== "object") return null;
  if (typeof subItem.reason === "string") return subItem.reason;
  if (typeof subItem.label === "string") return subItem.label;
  if (typeof subItem.text === "string") return subItem.text;
  const longText = Object.values(subItem).find(
    (v) => typeof v === "string" && v.length > 24 && !v.startsWith("http"),
  );
  return typeof longText === "string" ? longText : null;
}

/** Thumbnail-first node cell – no full HTML dump beside a tiny column. */
export function renderNodeCell(node, screenshot, onThumbnailClick) {
  if (!node || typeof node !== "object") return null;

  const hasThumb =
    screenshot &&
    node.boundingRect &&
    node.boundingRect.width > 0 &&
    node.boundingRect.height > 0;
  const summary = summarizeNodeSnippet(node.snippet);

  return (
    <div className="flex flex-col items-start gap-2 w-[88px] sm:w-24 shrink-0">
      {hasThumb && (
        <Thumbnail
          screenshot={screenshot}
          rect={node.boundingRect}
          onClick={() => onThumbnailClick(node.boundingRect)}
        />
      )}
      {!hasThumb && summary && (
        <code
          className="font-mono text-xs text-muted-foreground line-clamp-3 max-w-[12rem]"
          title={node.snippet}
        >
          {summary}
        </code>
      )}
      {node.nodeLabel && (
        <span className="text-xs text-muted-foreground line-clamp-2">
          {node.nodeLabel}
        </span>
      )}
    </div>
  );
}

export const getColumnValue = (item, heading, screenshot, onThumbnailClick) => {
  if (item.type === "checklist") {
    if (heading.key === "status") {
      return (
        <div className="flex items-center">
          {item.status ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-green-500"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-red-500"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </div>
      );
    }
    return item.text;
  }

  if (item.type === "node") {
    if (heading.key === "text") {
      return (
        <div className="flex flex-col space-y-2 max-w-md">
          {item.selector && (
            <div className="text-sm text-muted-foreground">{item.selector}</div>
          )}
          {item.snippet && (
            <code className="text-xs bg-muted p-2 rounded line-clamp-3" title={item.snippet}>
              {summarizeNodeSnippet(item.snippet)}
            </code>
          )}
        </div>
      );
    }
    return null;
  }

  const value = item[heading.key];
  const itemType = heading.itemType || heading.valueType;

  switch (itemType) {
    case "thumbnail":
      return <img src={value} alt="thumbnail" className="w-8 h-8" />;
    case "url": {
      const url =
        typeof value === "object" && value !== null ? value.url : value;
      if (!url || typeof url !== "string") return null;
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-brand hover:underline whitespace-nowrap md:whitespace-normal md:break-all"
          title={url}
        >
          {url}
        </a>
      );
    }
    case "node":
      if (!value || typeof value !== "object") return null;
      return renderNodeCell(value, screenshot, onThumbnailClick);
    case "source-location":
      if (value?.url) {
        const line = value.line ? `:${value.line}` : "";
        const column = value.column ? `:${value.column}` : "";
        return (
          <a
            href={value.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand hover:underline whitespace-nowrap md:whitespace-normal md:break-all"
            title={`${value.url}${line}${column}`}
          >
            {`${value.url.split("/").pop()}${line}${column}`}
          </a>
        );
      }
      return null;
    case "bytes":
      return formatBytes(value);
    case "ms":
      return formatTime(value);
    case "numeric":
      if (
        value &&
        typeof value === "object" &&
        typeof value.value === "number"
      ) {
        return new Intl.NumberFormat().format(value.value);
      }
      if (typeof value === "number") {
        return new Intl.NumberFormat().format(value);
      }
      break;
    default:
      if (typeof value === "object" && value !== null) return null;
      return value;
  }
};

export const renderElementContent = (row, screenshot, handleThumbnailClick) => {
  if (row.node?.type === "text" && row.node?.value === "Total") {
    return <span className="font-medium">Total</span>;
  }

  if (!row.node) return null;

  return (
    <div className="flex flex-col space-y-2 max-w-md">
      {row.node.nodeLabel && <div className="text-sm">{row.node.nodeLabel}</div>}
      {row.node.snippet && (
        <code className="font-mono text-xs line-clamp-3" title={row.node.snippet}>
          {summarizeNodeSnippet(row.node.snippet)}
        </code>
      )}
      {row.node.boundingRect && screenshot && (
        <div>
          <Thumbnail
            screenshot={screenshot}
            rect={row.node.boundingRect}
            onClick={() => handleThumbnailClick(row.node.boundingRect)}
          />
        </div>
      )}
    </div>
  );
};

export function hasLighthouseIssueContent(issueKey, issue) {
  if (!issue) return false;
  if (issue.description) return true;
  if (issue.items?.length) return true;
  if (issueKey === "documentLatencyInsight" && issue.details) return true;
  if (
    issueKey === "networkDependencyTreeInsight" &&
    issue.details?.items?.[0]?.value?.chains
  ) {
    return true;
  }
  if (issueKey === "thirdPartiesInsight" && issue.details?.items?.length) {
    return true;
  }
  return false;
}
